/**
 * `DataTable` (D529): a semantic `<table>` for analytic results with conditional formatting and in-cell marks — an inline bar,
 * a sparkline, a heat background — and threshold rules whose state is written as a SHAPE and a (hidden) word as well as a
 * colour. Numbers sit in the mono face, right-aligned. It scrolls sideways inside its own region on a phone.
 */
import { useMemo, type ReactElement } from 'react';
import { buildHeatGrid, fraction, isFiniteNumber } from '@vello/charts-core';
import { ChartToolbar, HeatLegend, chartState, emitSelect, heatFill, heatInk, type ChartSelectProps } from './bi-kit';
import type { ChartCsv, ChartExportProps } from './export';
import { cx, defaultFormatValue, toneColor, type ChartToneName } from './kit';
import type { ChartBaseProps } from './line-chart';
import { Sparkline } from './sparkline';

export type DataTableValue = string | number | null | undefined | readonly (number | null)[];

/** A state rule on a numeric cell: when it matches, the cell takes the tone, a marker shape and the word `label`. */
export interface DataTableRule {
  readonly when: 'above' | 'below' | 'between';
  readonly value: number;
  /** Upper bound of `between`. */
  readonly to?: number;
  readonly tone: Exclude<ChartToneName, 'neutral'>;
  /** The state in words (localized), read by assistive technology and used as the marker's title. */
  readonly label: string;
}

export interface DataTableColumn {
  readonly key: string;
  readonly header: string;
  /** `text` (default), `number`, `bar` (a number with an inline bar), `spark` (an array of numbers), `heat` (a number on a heat background). */
  readonly kind?: 'text' | 'number' | 'bar' | 'spark' | 'heat';
  readonly format?: (value: number) => string;
  readonly rules?: readonly DataTableRule[];
  /** For `bar` / `heat`: the value of a full bar / the top of the heat scale (default: the column maximum). */
  readonly max?: number;
  /** For `heat`: the bottom of the scale (default: the column minimum). */
  readonly min?: number;
  readonly align?: 'start' | 'end';
}

export interface DataTableRow {
  readonly key: string;
  readonly values: Readonly<Record<string, DataTableValue>>;
}

export interface DataTableProps extends ChartBaseProps, ChartSelectProps, ChartExportProps {
  readonly columns: readonly DataTableColumn[];
  readonly rows: readonly DataTableRow[];
  /** The visible caption (default: `title`, hidden visually but read by assistive technology). */
  readonly caption?: string;
  /** The column whose cell becomes a button that reports `{ dimension, value }` (the cell's text). */
  readonly selectColumn?: string;
  /** Show the low → high ramp under a table that has a heat column. */
  readonly showLegend?: boolean;
}

function ruleMatch(rule: DataTableRule, v: number): boolean {
  if (rule.when === 'above') return v > rule.value;
  if (rule.when === 'below') return v < rule.value;
  return v >= rule.value && v <= (rule.to ?? rule.value);
}

/** The shape marking a state: it differs by tone so the state is readable without colour. */
const MARKER: Readonly<Record<Exclude<ChartToneName, 'neutral'>, string>> = { success: '●', warning: '▲', danger: '■' };

function textOf(value: DataTableValue, column: DataTableColumn): string {
  if (value === null || value === undefined) return '–';
  if (typeof value === 'number') return (column.format ?? defaultFormatValue)(value);
  if (typeof value === 'string') return value;
  return value.filter(isFiniteNumber).map((n) => (column.format ?? defaultFormatValue)(n)).join(' ');
}

/** A table of analytic results with number formatting, heat, bars, sparklines and state rules. */
export function DataTable(props: DataTableProps): ReactElement {
  return chartState(props, 160, props.rows.length > 0 && props.columns.length > 0) ?? <DataTableBody {...props} />;
}

function DataTableBody(props: DataTableProps): ReactElement {
  const { columns, rows } = props;
  const heatColumns = columns.filter((c) => c.kind === 'heat');
  // Per-column scales are derived once: the maximum for bars, the heat levels for heat columns.
  const scales = useMemo(() => {
    const out = new Map<string, { max: number; levels: Map<string, number | null> }>();
    for (const c of columns) {
      const nums = rows.map((r) => r.values[c.key]).filter((v): v is number => isFiniteNumber(v));
      const max = c.max ?? Math.max(0, ...nums);
      const levels = new Map<string, number | null>();
      if (c.kind === 'heat') {
        const grid = buildHeatGrid({
          rows: rows.length,
          cols: 1,
          cells: rows.map((r, row) => ({ row, col: 0, value: isFiniteNumber(r.values[c.key]) ? (r.values[c.key] as number) : null })),
          ...(c.min !== undefined || c.max !== undefined ? { domain: [c.min ?? Math.min(...nums), c.max ?? Math.max(...nums)] as const } : {}),
        });
        rows.forEach((r, row) => levels.set(r.key, grid.cells[row]?.level ?? null));
      }
      out.set(c.key, { max, levels });
    }
    return out;
  }, [columns, rows]);

  const csv = (): ChartCsv => ({ header: columns.map((c) => c.header), rows: rows.map((r) => columns.map((c) => cellForCsv(r.values[c.key]))) });

  return (
    <div className={cx('vello-chart', 'vello-datatable', props.className)} data-motion="off">
      <ChartToolbar props={props} title={props.title} csv={csv} png={false} />
      <div className="vello-datatable__scroll" role="region" aria-label={props.title ?? props.caption} tabIndex={0}>
        <table className="vello-datatable__table">
          {(props.caption ?? props.title) !== undefined && <caption className="vello-chart__sr">{props.caption ?? props.title}</caption>}
          <thead>
            <tr>
              {columns.map((c) => (
                <th key={c.key} scope="col" className="vello-datatable__head" data-align={alignOf(c)}>
                  {c.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const chosen = props.selectColumn !== undefined && (props.selected?.includes(String(r.values[props.selectColumn] ?? '')) ?? false);
              return (
                <tr key={r.key} className="vello-datatable__row" {...(props.selectColumn !== undefined && props.selected !== undefined && props.selected.length > 0 ? { 'data-chosen': chosen ? 'true' : 'false' } : {})}>
                  {columns.map((c, ci) => (
                    <DataCell key={c.key} column={c} row={r} first={ci === 0} scale={scales.get(c.key)} props={props} />
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {(props.showLegend ?? true) && heatColumns.length > 0 && (() => {
        const first = heatColumns[0] as DataTableColumn;
        const nums = rows.map((r) => r.values[first.key]).filter((v): v is number => isFiniteNumber(v));
        const f = first.format ?? defaultFormatValue;
        return nums.length > 0 ? <HeatLegend min={f(first.min ?? Math.min(...nums))} max={f(first.max ?? Math.max(...nums))} /> : null;
      })()}
    </div>
  );
}

function cellForCsv(v: DataTableValue): string | number | null {
  if (v === null || v === undefined) return null;
  if (typeof v === 'number' || typeof v === 'string') return v;
  return v.filter(isFiniteNumber).join(' ');
}

function alignOf(c: DataTableColumn): 'start' | 'end' {
  return c.align ?? (c.kind === undefined || c.kind === 'text' ? 'start' : 'end');
}

function DataCell({ column, row, first, scale, props }: { column: DataTableColumn; row: DataTableRow; first: boolean; scale: { max: number; levels: Map<string, number | null> } | undefined; props: DataTableProps }): ReactElement {
  const value = row.values[column.key];
  const kind = column.kind ?? 'text';
  const format = column.format ?? defaultFormatValue;
  const align = alignOf(column);
  const Tag = first ? 'th' : 'td';
  const tagProps = first ? ({ scope: 'row' } as const) : {};
  const num = isFiniteNumber(value) ? value : null;
  const rule = num === null ? undefined : column.rules?.find((r) => ruleMatch(r, num));
  const level = kind === 'heat' ? (scale?.levels.get(row.key) ?? null) : null;
  const selectable = props.selectColumn === column.key && props.onSelect !== undefined && value !== null && value !== undefined;

  let body: ReactElement;
  if (kind === 'spark' && Array.isArray(value)) {
    body = (
      <span className="vello-datatable__spark">
        <Sparkline values={value as readonly (number | null)[]} height={28} animate={false} ariaLabel={column.header + ': ' + textOf(value, column)} />
      </span>
    );
  } else if (kind === 'bar' && num !== null) {
    const share = fraction(Math.max(0, num), scale?.max ?? 0);
    body = (
      <span className="vello-datatable__bar">
        <span className="tnum vello-datatable__figure">{format(num)}</span>
        <span className="vello-datatable__bar-track" aria-hidden="true">
          <span className="vello-datatable__bar-fill" style={{ width: `${share * 100}%` }} />
        </span>
      </span>
    );
  } else if (num !== null && (kind === 'number' || kind === 'heat' || kind === 'bar')) {
    body = <span className="tnum vello-datatable__figure">{format(num)}</span>;
  } else {
    body = <span>{textOf(value, column)}</span>;
  }

  const content = (
    <>
      {rule !== undefined && (
        <span className="vello-datatable__marker" aria-hidden="true">
          {MARKER[rule.tone]}
        </span>
      )}
      {body}
      {rule !== undefined && <span className="vello-chart__sr">{rule.label}</span>}
    </>
  );

  return (
    <Tag
      {...tagProps}
      className="vello-datatable__cell"
      data-align={align}
      data-kind={kind}
      data-tone={rule?.tone}
      style={{
        ...(level !== null ? { background: heatFill(level), color: heatInk(level) } : {}),
        // A heat background keeps its audited ink: the state then reads from the marker shape and the hidden word, not from a colour on a colour.
        ...(rule !== undefined && level === null ? { color: toneColor(rule.tone) } : {}),
        // The state is also a weight: bold figure + the marker shape + the hidden word, so it never depends on a colour (and on a heat fill the colour is the audited ink).
        ...(rule !== undefined ? { fontWeight: 700 } : {}),
      }}
    >
      {selectable ? (
        <button
          type="button"
          className="vello-datatable__select"
          aria-pressed={props.selected?.includes(String(value)) ?? false}
          onClick={() => emitSelect(props, String(value), props.dimension ?? column.key)}
        >
          {content}
        </button>
      ) : (
        <span className="vello-datatable__content">{content}</span>
      )}
    </Tag>
  );
}
